<template>
  <v-container fluid>
    <PageHeader title="Connections" icon="mdi-domain" />
    <v-card>

      <div class="ml-4 mb-4">
        <div>
          <v-row class="ma-0 pa-0">
            <v-col sm="12" cols="12" md="4" class="pa-0 pr-4">
              <v-text-field
                v-model="searchQuery"
                prepend-icon="mdi-magnify"
                :label="searchLabel"
                @keydown="() => (tableOptions.page = 1)"
                single-line
                hide-details
                variant="underlined"
              ></v-text-field>
            </v-col>
            <v-col sm="12" cols="12" md="4" class="pa-0" v-if="false">
              <v-select
                class="mr-4"
                prepend-icon="mdi-filter"
                v-model="selectedPartyTypeId"
                :items="partyTypeList"
                item-title="name"
                item-value="id"
              ></v-select>
            </v-col>
            <v-col sm="12" cols="12" md="3" class="pa-0">
              <v-select
                v-show="!onlyPossibleConnections"
                :items="partyStatuses"
                class="mr-4"
                v-model="selectedConnectionStatus"
                variant="underlined"
                hide-details
              ></v-select>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-data-table-server
        :must-sort="true"
        :no-data-text="''"
        :headers="headers.filter(h => h.show !== false)"
        :items="enhancedParties"
        item-key="id"
        :loading="loading"
        show-select
        v-model:options="tableOptions"
        :items-length="totalRecords"
        :items-per-page-options="[25, 50, 75, 100, -1]"
        @click:row="(event, { item }) => selectParty(item)"
        class="mb-16"
      >
        <template v-slot:item.icon="{ item }">
          <v-icon v-show="item.isDisconnected">mdi-domain</v-icon>
          <v-icon v-show="partyStatusService.isConnected(item)" color="success">mdi-domain</v-icon>
          <v-icon v-show="partyStatusService.isOnNetwork(item) || partyStatusService.IsConnecting(item)" color="primary"
            >mdi-domain</v-icon
          >
          <v-icon v-show="partyStatusService.isNotOnNetwork(item)">mdi-domain</v-icon>
          <v-icon v-show="partyStatusService.IsInviteSent(item)">mdi-email-open-outline</v-icon>
          <v-icon v-show="partyStatusService.isInviteExpired(item)">mdi-lock-clock </v-icon>
          <v-icon v-show="partyStatusService.isConnectionExpired(item)">mdi-lock-clock </v-icon>
        </template>

        <template v-slot:item.partyTypeName="{ item }">
          {{ partyTypeNameWithTerminology(item.partyTypeId, item.partyTypeName) }}
        </template>
        <template v-slot:item.name="{ item }">
          <div v-html="highlight(item.name, searchQuery)"></div>
        </template>
        <template v-slot:item.number="{ item }">
          <div v-html="highlight(item.number, searchQuery)"></div>
        </template>
        <template v-slot:item.phone="{ item }">
          <div v-html="highlight(item.phone, searchQuery)"></div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="mt-1">
            <PartyStatus :small="true" :party="item" :callback="doneAction" />
          </div>
        </template>
        <template v-slot:item.effectiveFromDate="{ item }">
          <span v-if="item.effectiveFromDate">{{
            $filters.moment(item.effectiveFromDate, dateFormat.toUpperCase())
          }}</span>
        </template>
        <template v-slot:item.effectiveThruDate="{ item }">
          <span v-if="item.effectiveThruDate">{{
            $filters.moment(item.effectiveThruDate, dateFormat.toUpperCase())
          }}</span>
        </template>

        <template v-slot:item.isActive="{ item }">
          {{ item.isActive ? "yes" : "no" }}
        </template>

        <template v-slot:item.companyOrgUnits="{ item }">
          <div v-html="highlight(item.companyOrgUnits, searchQuery)"></div>
        </template>
      </v-data-table-server>
    </v-card>

    <v-dialog v-model="showNetworkMatchesDialog" :max-width="1000">
      <v-form ref="form">
        <v-card>
          <v-card-text class="text-h6 pt-4">{{ networkMatchesDialogTitle }}</v-card-text>
          <v-card-text>
            <LoadingProgress :loading="networkMatchesLoading" />
          </v-card-text>
          <v-card-text>
            <v-data-table :must-sort="true" :no-data-text="''" :headers="networkMatchesHeaders" :items="networkMatches">
              <template v-slot:item.actions="{ item }">
                <div class="mt-1">
                  <PartyStatus :small="true" :party="item" :callback="connectedToParty" />
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-form>
    </v-dialog>
  </v-container>
</template>

<script>
import partyService from "@/services/partyService.js";
import PageHeader from "@/components/PageHeader.vue";
import partyStatusService from "@/services/partyStatusService.js";
import Auth0Mixin from "../Auth0Mixin";
import filterService from "@/services/filterService.js";
import partyType from "@/constants/partyType";
import partyTypeList from "@/constants/partyTypeList";
import searchUtilsService from "@/services/searchUtilsService.js";
import localStorageService from "@/services/localStorageService.js";
import PartyStatus from "@/components/PartyStatus.vue";
import LoadingProgress from "@/components/LoadingProgress.vue";
import terminologyService from "@/services/terminologyService.js";
import terminology from "@/constants/terminology";
import { mapGetters } from "vuex";
import validationService from "@/validationRules";

const partyTypeListWithTerminology = JSON.parse(JSON.stringify(partyTypeList));

export default {
  name: "Connections",
  mixins: [Auth0Mixin],
  components: {
    PartyStatus,
    LoadingProgress,
    PageHeader,
  },
  props: {
    onlyPossibleConnections: Boolean,
  },

  data() {
    return {
      requiredRules: [validationService.requiredRule, validationService.emailRule],
      searchQuery: "",
      orgUnitTerminology: "",
      headers: [
        { title: "", key: "icon", width: 40, sortable: false },
        { title: "Type", key: "partyTypeName", sortable: false, show: false },
        { title: "Connection Name", key: "name" },
        { title: "Company", key: "connectedCompany" },
        { title: "Number", key: "number", show: false },
        { title: "Phone", key: "phone" },
        { title: "", key: "companyOrgUnits" },
        { title: "Effective From Date", key: "effectiveFromDate" },
        { title: "Effective Thru Date", key: "effectiveThruDate" },

        { title: "Active", key: "isActive" },
        { title: "", key: "actions", sortable: false },
      ],
      networkMatchesHeaders: [
        { title: "Type", key: "partyTypeName", sortable: false },
        { title: "Name", key: "name" },
        { title: "Number", key: "number" },
        { title: "", key: "actions", sortable: false },
      ],
      invitePartiesHeaders: [
        { title: "Name", key: "name" },
        { title: "Number", key: "number" },
        { title: "Invite email", key: "inviteEmailAddress", sortable: false },
      ],
      tableOptions: {
        sortBy: [{ key: "name", order: "asc" }],
        itemsPerPage: 50,
      },
      initialized: false,
      parties: [],
      showInviteDialog: false,
      totalRecords: 0,
      loading: true,
      highlight: filterService.highlight,
      currentRequest: null,
      selectedPartyTypeId: -1,
      partyTypeList: [{ id: -1, name: "- All Connection Types -" }, ...partyTypeListWithTerminology],
      partyStatusService,
      showNetworkMatchesDialog: false,
      networkMatchesLoading: false,
      networkMatches: [],
      mustBeConnected: false,
      selectedConnectionStatus: "",
      connectStatus: { title: "Connect", value: "onNetwork" },
      partyStatuses: [
        { title: "- All Connection Statuses -", value: "" },
        { title: "Disconnected", value: "disconnected" },
        { title: "Connected", value: "connected" },
        { title: "Invite Sent", value: "inviteSent" },
        { title: "Waiting for other connection to accept", value: "connecting" },
        { title: "Invite expired", value: "inviteExpired" },
        { title: "Connection request expired", value: "connectionRequestExpired" },
      ],
    };
  },

  watch: {
    tableOptions: {
      handler() {
        this.saveFiltersAndPaging();
        this.search();
      },
      deep: true,
    },
    selectedPartyTypeId() {
      this.saveFiltersAndPaging();
      this.search();
    },
    selectedConnectionStatus() {
      this.saveFiltersAndPaging();
      this.search();
    },
    mustBeConnected() {
      this.search();
    },
    searchQuery() {
      if (this.currentRequest) {
        clearTimeout(this.currentRequest);
      }

      this.saveFiltersAndPaging();
      this.currentRequest = setTimeout(async () => {
        await this.search();
      }, 200);
    },
    async onlyPossibleConnections() {
      await this.init();
    },
  },

  computed: {
    ...mapGetters("userProfile", ["dateFormat"]),

    searchLabel() {
      let partyTypeId = this.selectedPartyTypeId;
      let p = partyTypeList.find(pt => pt.id === partyTypeId);

      return searchUtilsService.getLabel(p ? `${p.name}s` : "Connections", this.totalRecords);
    },

    networkMatchesDialogTitle() {
      if (this.networkMatchesLoading) return "Finding Matches on the Exchange Network...";

      if (this.networkMatches.length === 0) return "Sorry no matches found";

      return this.networkMatches.length === 1 ? "1 match found" : `${this.networkMatches.length} matches found`;
    },

    enhancedParties: function() {
      return this.parties.map(party => ({
        isSelectable: partyStatusService.isNotOnNetwork(party),
        ...party,
      }));
    },
  },

  methods: {
    async init() {
      this.headers[6].title = terminologyService.lookup(terminology.ORG_UNIT);

      partyTypeListWithTerminology.forEach(pt => {
        if (pt.terminologyId) pt.name = terminologyService.lookup(pt.terminologyId);
      });

      let options = localStorageService.get("partyList", {
        page: 1,
        itemsPerPage: 50,
        sortBy: [{ key: "name", order: "asc" }],
      });
      this.tableOptions = options;
      this.searchQuery = options.searchQuery;
      this.selectedPartyTypeId = options.partyTypeId || -1;
      this.initialized = true;
      this.selectedConnectionStatus = options.connectionStatus || "";
      this.search();
    },

    partyTypeNameWithTerminology(partyTypeId, partyTypeName) {
      return partyTypeId === partyType.VENDOR ? terminologyService.lookup(terminology.VENDOR) : partyTypeName;
    },

    async doneAction() {
      this.search();
    },

    async connectedToParty() {
      this.search();
    },

    saveFiltersAndPaging() {
      localStorageService.save("partyList", this.tableOptions, {
        searchQuery: this.searchQuery,
        partyTypeId: this.selectedPartyTypeId,
        connectionStatus: this.selectedConnectionStatus,
      });
    },

    async search() {
      if (!this.initialized) return;

      // If we're in the possible connections section, then force the search to only be for the "CONNECT" status
      let connectionStatus = this.onlyPossibleConnections ? this.connectStatus.value : this.selectedConnectionStatus;

      const result = await partyService.search(
        false,
        this.searchQuery,
        this.selectedPartyTypeId,
        connectionStatus,
        this.tableOptions
      );

      // If we're in the regular connections (party) section, don't include connections with the "CONNECT" status
      this.parties =
        connectionStatus === "" && !this.onlyPossibleConnections
          ? result.items.filter(p => p.partyStatus !== this.connectStatus.value)
          : result.items;

      this.totalRecords = this.parties.length;
      this.loading = false;
    },

    selectParty(party) {
      this.$router.push(`/admin/connection/${party.id}`);
    },
  },
};
</script>
